import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Typography, Button, IconButton, Select, MenuItem, FormControl, InputLabel,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
    FormControlLabel, List, ListItem, ListItemText, Alert, CircularProgress,
    Switch, Tooltip, Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import ConfirmDialog from "../../components/ConfirmDialog";
import AssignToField from "./AssignToField";
import ScheduleV2TestsDialog from "./ScheduleV2TestsDialog";
import { Field } from "./prepUi";
import { fmtNyDateTime, nyToday } from "./prepDates";

const V2_API = `${process.env.REACT_APP_URL}/api/v2`;

const defaultForm = () => {
    const today = nyToday();
    return {
        prep_program_course_id: "", release_date: `${today}T00:00`, due_date: `${today}T23:59`,
        assign: { assign_mode: "ALL", student_ids: [] },
        // Adopt a test already assigned to this course (rather than skipping it as a conflict), so
        // work students have already done shows up in the program. Off by default.
        adopt_existing: false,
        include_prior_attempts: true,
    };
};

// v2 test-category folders form a tree (parent_id); flatten depth-first into an indented list so
// subfolders are visible/selectable in the dropdown.
const flattenFolders = (nodes, depth = 0, acc = []) => {
    (nodes || []).forEach((n) => {
        acc.push({ id: n.id, name: n.name, depth });
        if (n.children && n.children.length) flattenFolders(n.children, depth + 1, acc);
    });
    return acc;
};

// Pre-assign Test-Library (v2) tests (vocab & any v2 test) to a prep program's students, scoped to
// one of the program's courses. courses = [{ id (prep_program_course id), role, label, course_title }].
const ProgramV2TestsPanel = ({ programUuid, courses = [], onChange }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");        // dialog-scoped (only rendered inside the Add dialog)
    const [rowError, setRowError] = useState("");  // panel-scoped: row toggles / delete, which run with no dialog open
    const [success, setSuccess] = useState("");
    const [saving, setSaving] = useState({});      // row id -> true while its PUT is in flight
    const confirmRef = useRef();

    const [addOpen, setAddOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [form, setForm] = useState(defaultForm());
    const [folders, setFolders] = useState([]);
    const [folderId, setFolderId] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [libTests, setLibTests] = useState([]);
    const [libLoading, setLibLoading] = useState(false);
    const [checked, setChecked] = useState({}); // testId -> bool
    // What the server would do with each test for the chosen course — so the picker can label a
    // test up front instead of reporting it after the fact.
    const [courseState, setCourseState] = useState({ in_program: [], on_course: [] });

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await Axios.get(`${base}/v2-tests`, auth);
            setItems(res.data || []);
        } catch (e) {
            console.error("List v2 tests error", e);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const courseName = (id) => {
        const c = courses.find((x) => x.id === id);
        return c ? `${c.label || c.course_title || "Course"}${c.role ? ` · ${c.role}` : ""}` : `Course ${id}`;
    };

    const loadTests = useCallback(async () => {
        setLibLoading(true);
        try {
            const res = await Axios.get(`${V2_API}/tests`, { ...auth, params: { category_id: folderId || undefined, include_descendants: folderId ? true : undefined, search: debouncedSearch || undefined, limit: 300 } });
            setLibTests(res.data?.data || []);
        } catch (e) {
            setLibTests([]);
        } finally {
            setLibLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderId, debouncedSearch]);

    useEffect(() => { if (addOpen) loadTests(); }, [addOpen, loadTests]);

    // Re-read whenever the target course changes — "already on the course" is per course, and after
    // an add, since what just landed becomes "already added".
    const courseId = form.prep_program_course_id;
    const loadCourseState = useCallback(async () => {
        if (!courseId) { setCourseState({ in_program: [], on_course: [] }); return; }
        try {
            const res = await Axios.get(`${base}/v2-tests/course-state`, { ...auth, params: { prep_program_course_id: courseId } });
            setCourseState({ in_program: res.data?.in_program || [], on_course: res.data?.on_course || [] });
        } catch (e) {
            setCourseState({ in_program: [], on_course: [] }); // unknown → label nothing, never mislabel
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base, courseId]);

    useEffect(() => { if (addOpen) loadCourseState(); }, [addOpen, loadCourseState]);
    // Debounce the search box: fetch once typing pauses, not on every keystroke.
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(id);
    }, [search]);

    const openAdd = async () => {
        setForm({ ...defaultForm(), prep_program_course_id: courses[0]?.id || "" });
        setFolderId(""); setSearch(""); setDebouncedSearch(""); setLibTests([]); setChecked({}); setError(""); setSuccess("");
        setAddOpen(true);
        try {
            const res = await Axios.get(`${V2_API}/test-categories`, { ...auth, params: { tree: true } });
            setFolders(flattenFolders(res.data?.data || []));
        } catch (e) {
            setFolders([]);
        }
    };

    // What happens to one test on Add: already configured here (nothing to do), already owned by the
    // course (adopted or skipped, depending on the checkbox), or added fresh with the dates above.
    const inProgram = new Set(courseState.in_program);
    const onCourse = new Set(courseState.on_course);
    const testFate = (id) => (inProgram.has(id) ? "in_program" : onCourse.has(id) ? "on_course" : "fresh");

    const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));
    // Already-configured tests are a no-op, so bulk-select skips them rather than padding the count.
    const selectAll = () => setChecked((p) => ({
        ...p,
        ...Object.fromEntries(libTests.filter((t) => testFate(t.id) !== "in_program").map((t) => [t.id, true])),
    }));
    const clearAll = () => setChecked({});
    const checkedIds = Object.keys(checked).filter((k) => checked[k]).map(Number);
    // Split the selection the way the server will, for the Add button and the summary line.
    const willAdopt = checkedIds.filter((id) => testFate(id) === "on_course");
    const willCreate = checkedIds.filter((id) => testFate(id) === "fresh");
    // Nothing lands when the only thing ticked is already on the course and adopt is off — don't
    // offer an Add that would be a no-op.
    const willLand = willCreate.length + (form.adopt_existing ? willAdopt.length : 0);

    const handleAdd = async () => {
        setError(""); setSuccess("");
        if (!form.prep_program_course_id) { setError("Pick a course"); return; }
        if (!checkedIds.length) { setError("Pick at least one test"); return; }
        try {
            const res = await Axios.post(`${base}/v2-tests`, {
                prep_program_course_id: form.prep_program_course_id,
                v2_test_ids: checkedIds,
                assign_mode: form.assign.assign_mode,
                student_ids: form.assign.assign_mode === "SELECTED" ? form.assign.student_ids : [],
                release_date: form.release_date || null,
                due_date: form.due_date || null,
                adopt_existing: form.adopt_existing,
                include_prior_attempts: form.include_prior_attempts,
            }, auth);
            const conflictTests = res.data?.conflict_tests || [];
            const nameList = (rows) => rows.map((t) => t.title).filter(Boolean).join(", ");
            const adoptedNames = nameList(res.data?.adopted_tests || []);
            setSuccess(`Added ${res.data?.created ?? 0} test(s)`
                + (res.data?.adopted
                    // Name them: an adopted test ignores the release/due/roster set above, and a
                    // silent count leaves the admin unable to tell which ones that applied to.
                    ? `. Adopted ${res.data.adopted} already on this course${adoptedNames ? ` (${adoptedNames})` : ""}`
                      + " — those keep the existing assignment's dates and roster, not the ones set here."
                    : "")
                + (res.data?.skipped ? `. ${res.data.skipped} already added` : "")
                + (conflictTests.length || res.data?.conflicts
                    ? `. ${res.data.conflicts} already assigned to that course${nameList(conflictTests) ? ` (${nameList(conflictTests)})` : ""}`
                      + " — still selected: tick \"Adopt tests already on this course\" and press Add to pull them in with the work students have done."
                    : ""));
            // Keep exactly the conflicting tests selected so that instruction can actually be
            // followed; anything that landed is cleared. Adoption never carries to the next batch —
            // it silently rewrites dates/roster, so it must be a per-submission decision.
            setChecked(Object.fromEntries(conflictTests.map((t) => [t.v2_test_id, true])));
            setForm((f) => ({ ...f, adopt_existing: false, include_prior_attempts: true }));
            await fetchItems();
            await loadCourseState(); // what just landed is now "already in this program"
            if (onChange) onChange();
        } catch (e) {
            console.error("Add v2 tests error", e);
            setError(e.response?.data?.error || "Failed to add tests");
        }
    };

    // Optimistic display-flag write for one row. Both flags are local to this panel — the parent's
    // program payload carries neither — so this deliberately does NOT call onChange(): that runs the
    // editor's loadDetail() and would throw away whatever the admin has typed on the Basics tab.
    // The row is locked while its PUT is in flight, so a double-flip can't land out of order.
    const saveRowFlag = async (item, patch, label) => {
        if (saving[item.id]) return;
        const before = { include_prior_attempts: item.include_prior_attempts, show_in_exam_tab: item.show_in_exam_tab };
        setRowError("");
        setSaving((p) => ({ ...p, [item.id]: true }));
        setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, ...patch } : t))); // optimistic
        try {
            await Axios.put(`${base}/v2-tests/${item.id}`, patch, auth);
        } catch (e) {
            setItems((prev) => prev.map((t) => (t.id === item.id ? { ...t, ...before } : t))); // roll back
            setRowError(`Couldn't save "${label}" for ${item.test_title || "this test"} — it's unchanged. Try again.`);
        } finally {
            setSaving((p) => { const n = { ...p }; delete n[item.id]; return n; });
        }
    };

    // "Also show in Practice Exams" — the test stays on the Library Tests tab either way; this
    // only adds a row to the exam matrix (for library-built mock exams).
    const toggleExamTab = (item, next) => saveRowFlag(item, { show_in_exam_tab: next }, "In Practice Exams");
    // Display-only, so it's safe to flip after the fact.
    const togglePrior = (item, next) => saveRowFlag(item, { include_prior_attempts: next }, "Prior attempts");

    const doDelete = async (id) => {
        setRowError("");
        try {
            await Axios.delete(`${base}/v2-tests/${id}`, auth);
            setItems((prev) => prev.filter((t) => t.id !== id));
            if (onChange) onChange();
        } catch (e) {
            setRowError("Couldn't remove the test — it's still in the program. Try again.");
        }
    };
    const confirmDelete = (item) => confirmRef.current.open({
        title: "Remove test?",
        message: `Remove "${item.test_title || "this test"}" from this program?`
            + (item.adopted_at
                ? " It was adopted from this course's existing assignment — that assignment and every submission stay exactly as they are; only the program's view of it goes away."
                : ""),
        confirmLabel: "Remove",
        onConfirm: () => doDelete(item.id),
    });

    // An adopted row is a read-only view onto an assignment the course already had: its dates and
    // roster are the owner's, so describing ours would be a lie.
    const secondary = (t) =>
        t.adopted_at
            ? `${courseName(t.prep_program_course_id)} · adopted from this course's existing assignment`
                + ` · prior attempts ${t.include_prior_attempts ? "included" : "ignored"}`
                + (t.materialized ? "" : " · link lost (assignment removed)")
            : `${courseName(t.prep_program_course_id)} · ${t.assign_mode === "SELECTED" ? `${(t.student_ids || []).length} students` : "All enrolled"}`
                + (t.release_date ? ` · releases ${fmtNyDateTime(t.release_date)}` : "")
                + (t.due_date ? ` · due ${fmtNyDateTime(t.due_date)}` : "")
                + (t.materialized ? " · live" : " · scheduled");

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Library tests (vocab &amp; more)</Typography>
            {rowError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRowError("")}>{rowError}</Alert>}
            {!courses.length && <Typography variant="body2" color="text.secondary">Add and save a course first.</Typography>}
            {loading ? <CircularProgress size={20} /> : items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No library tests assigned yet.</Typography>
            ) : (
                <List dense>
                    {/* The controls sit in normal flow rather than in `secondaryAction`: that renders
                        absolutely positioned against only 48px of reserved padding, so a row of
                        switches paints straight over the title. Flow + wrap keeps them clear. */}
                    {items.map((t) => (
                        <ListItem
                            key={t.id}
                            divider
                            sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, py: 1 }}
                        >
                            <ListItemText
                                sx={{ flex: "1 1 18rem", my: 0 }}
                                disableTypography
                                primary={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                        <Typography variant="body2">{t.test_title || `Test ${t.v2_test_id}`}</Typography>
                                        {t.adopted_at && <Chip size="small" label="Adopted" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                                    </Box>
                                }
                                secondary={<Typography variant="caption" color="text.secondary">{secondary(t)}</Typography>}
                            />
                            {/* gap 2, not 0.5: with two labelled switches side by side the first
                                label ran straight into the second switch. */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0, ml: "auto" }}>
                                {t.adopted_at && (
                                    <Tooltip title="Count attempts students made before this test was adopted into the program">
                                        <FormControlLabel
                                            sx={{ mr: 0 }}
                                            control={<Switch size="small" disabled={!!saving[t.id]} checked={!!t.include_prior_attempts} onChange={(e) => togglePrior(t, e.target.checked)} />}
                                            label={<Typography variant="caption" color="text.secondary">Prior attempts</Typography>}
                                        />
                                    </Tooltip>
                                )}
                                <Tooltip title="Also show this test as a row in the Practice Exams tab (it stays here either way)">
                                    <FormControlLabel
                                        sx={{ mr: 0 }}
                                        control={<Switch size="small" disabled={!!saving[t.id]} checked={!!t.show_in_exam_tab} onChange={(e) => toggleExamTab(t, e.target.checked)} />}
                                        label={<Typography variant="caption" color="text.secondary">In Practice Exams</Typography>}
                                    />
                                </Tooltip>
                                <IconButton onClick={() => confirmDelete(t)}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                        </ListItem>
                    ))}
                </List>
            )}

            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAdd} disabled={!courses.length}>Add tests</Button>
                <Button variant="outlined" size="small" startIcon={<CalendarMonthIcon />} onClick={() => setScheduleOpen(true)} disabled={!courses.length}>Add on a schedule</Button>
            </Box>

            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Add library tests</DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                    <Field label="Assign to course">
                        <FormControl size="small" fullWidth>
                            <Select value={form.prep_program_course_id} onChange={(e) => setForm((f) => ({ ...f, prep_program_course_id: e.target.value }))}>
                                {courses.map((c) => <MenuItem key={c.id} value={c.id}>{courseName(c.id)}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Field>
                    <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Folder</InputLabel>
                            <Select label="Folder" value={folderId} onChange={(e) => setFolderId(e.target.value)}>
                                <MenuItem value="">All folders</MenuItem>
                                {folders.map((f) => <MenuItem key={f.id} value={f.id} sx={{ pl: 2 + (f.depth || 0) * 2 }}>{f.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField size="small" label="Search tests" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flexGrow: 1 }} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                        <Button size="small" onClick={selectAll} disabled={!libTests.length}>Select all in view</Button>
                        <Button size="small" onClick={clearAll} disabled={!checkedIds.length}>Clear</Button>
                        <Typography variant="caption" color="text.secondary">
                            {checkedIds.length} selected
                            {willAdopt.length
                                ? ` · ${willAdopt.length} already on this course (${form.adopt_existing ? "will be adopted" : "will be skipped"})`
                                : ""}
                        </Typography>
                    </Box>
                    <Box sx={{ maxHeight: 240, overflow: "auto", border: "1px solid #eee", borderRadius: 1, mt: 1, p: 1 }}>
                        {libLoading ? <CircularProgress size={18} /> : libTests.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No tests.</Typography>
                        ) : libTests.map((t) => {
                            const fate = testFate(t.id);
                            return (
                                <FormControlLabel
                                    key={t.id}
                                    sx={{ display: "flex", ml: 0, mr: 0, alignItems: "center" }}
                                    disabled={fate === "in_program"}
                                    control={<Checkbox size="small" checked={!!checked[t.id]} onChange={() => toggle(t.id)} />}
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                            <Typography variant="body2">{t.title}</Typography>
                                            {fate === "in_program" && (
                                                <Typography variant="caption" color="text.disabled">already in this program</Typography>
                                            )}
                                            {fate === "on_course" && (
                                                <>
                                                    <Chip size="small" label="on this course" variant="outlined"
                                                        sx={{ height: 18, fontSize: 10 }} />
                                                    <Typography variant="caption"
                                                        color={form.adopt_existing ? "success.main" : "warning.main"}>
                                                        {form.adopt_existing
                                                            ? "will be adopted — keeps its own dates and roster"
                                                            : "will be skipped — tick Adopt below to pull it in"}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    }
                                />
                            );
                        })}
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <TextField size="small" type="datetime-local" label="Release" InputLabelProps={{ shrink: true }}
                            value={form.release_date} onChange={(e) => setForm((f) => ({ ...f, release_date: e.target.value }))} />
                        <TextField size="small" type="datetime-local" label="Due" InputLabelProps={{ shrink: true }}
                            value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <AssignToField programUuid={programUuid} rosterParams={{ program_course_id: form.prep_program_course_id }}
                            value={form.assign} onChange={(v) => setForm((f) => ({ ...f, assign: v }))} />
                    </Box>
                    {/* These stay editable whatever the adopt checkbox says: a batch is usually all
                        fresh, and even a mixed one needs them for its fresh half. Now that the picker
                        knows the split, say exactly how many they'll reach. */}
                    {willAdopt.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            Applies to the {willCreate.length} test{willCreate.length === 1 ? "" : "s"} being added fresh.
                            The {willAdopt.length} marked “on this course” keep{willAdopt.length === 1 ? "s" : ""} the
                            dates and roster of the assignment already there.
                        </Typography>
                    )}
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #eee" }}>
                        <FormControlLabel
                            control={<Checkbox size="small" checked={form.adopt_existing}
                                onChange={(e) => setForm((f) => ({ ...f, adopt_existing: e.target.checked }))} />}
                            label={<Typography variant="body2">Adopt tests already on this course</Typography>}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4, mt: -0.5 }}>
                            Normally a test already assigned to this course is skipped. Adopt it instead to pull in the
                            work students have already done. The existing assignment stays in charge of its own dates and
                            roster — the program only reads it, and removing it here never deletes it.
                        </Typography>
                        {form.adopt_existing && (
                            <FormControl size="small" sx={{ mt: 1.5, ml: 4, minWidth: 320 }}>
                                <InputLabel>Attempts made before now</InputLabel>
                                <Select
                                    label="Attempts made before now"
                                    value={form.include_prior_attempts ? "include" : "ignore"}
                                    onChange={(e) => setForm((f) => ({ ...f, include_prior_attempts: e.target.value === "include" }))}
                                >
                                    <MenuItem value="include">Include them — show the scores already recorded</MenuItem>
                                    <MenuItem value="ignore">Ignore them — only count attempts from now on</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Close</Button>
                    {/* Name the outcome, not the selection: with adopt off, some of what's ticked
                        won't land, and the button shouldn't promise otherwise. */}
                    <Button variant="contained" onClick={handleAdd} disabled={!willLand}>
                        {form.adopt_existing && willAdopt.length
                            ? `Add ${willCreate.length}, adopt ${willAdopt.length}`
                            : `Add${willCreate.length ? ` ${willCreate.length}` : ""}`}
                    </Button>
                </DialogActions>
            </Dialog>

            <ScheduleV2TestsDialog
                open={scheduleOpen}
                onClose={() => setScheduleOpen(false)}
                programUuid={programUuid}
                courses={courses}
                existing={items}
                onCreated={() => { fetchItems(); if (onChange) onChange(); }}
            />
            <ConfirmDialog ref={confirmRef} />
        </Box>
    );
};

export default ProgramV2TestsPanel;
